import { ChangeDetectorRef, Component, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { combineLatest, Subscription } from 'rxjs';
import { FormValueService } from 'src/app/common/guards/save-data-guard/form-value.service';
import { IDeactivate } from 'src/app/common/guards/save-data-guard/save-data.interface';
import { OperationInfoService } from 'src/app/common/long-operations/operation-info.service';
import ProjectLinks from 'src/app/common/project.links';
import { DialogActionTypes } from 'src/app/notifications/dialogs/dialogs.enum';
import { NotificationContextAttributesService } from 'src/app/notifications/notifications-context-attributes.service';
import { safelyDetectChanges, unsubscribeAll } from '../../../common/app.helpers';
import { FormConstructorComponent } from '../../../forms/form-constructors/form-constructor/form-constructor.component';
import { FormStateService } from '../../../forms/form-state.service';
import { FormConstructorContent } from '../../../interfaces/form-and-field.interface';
import { PostSocialContext } from '../../../interfaces/posts.interface';
import { Post, SocialContextEntityType, UpdatePostRequest } from '../../../swagger-api';
import { PostsService } from '../posts.service';
import { UpdatePostService } from './update-post.service';

@Component({
  selector: 'app-update-post',
  templateUrl: './update-post.component.html',
  styleUrls: ['./update-post.component.scss'],
})
export class UpdatePostComponent implements OnInit, OnDestroy, IDeactivate {
  @Input({ required: true }) post: Post;

  public readonly LINK_POSTS_VIEW = ProjectLinks.LINK_POSTS_VIEW;

  public isFormChanged = false;
  public previousFormValue: object;
  public formContent: FormConstructorContent;

  private subscriptions$: Subscription[] = [];

  @ViewChild(FormConstructorComponent, { static: false })
  private updatePostForm: FormConstructorComponent;

  private updatePostService = inject(UpdatePostService);
  private formStateService = inject(FormStateService);
  private postsService = inject(PostsService);
  private cdr = inject(ChangeDetectorRef);
  private formValueService = inject(FormValueService);
  private operationInfoService = inject(OperationInfoService);
  private modalAttributesService = inject(NotificationContextAttributesService);

  public ngOnInit(): void {
    this.initSocialContex();

    this.subscribeOnUpdatePostServiceChanges();
  }

  public ngOnDestroy(): void {
    unsubscribeAll(this.subscriptions$);
  }

  public cancel(): void {
    this.updatePostService.cancel();
  }

  public onRemovePost(): void {
    this.operationInfoService.setupOperation(
      {
        successfulMessage: 'POST_CONFIRM_REMOVE_HINT',
        confirmationDialog: {
          description: 'POSTS_REMOVE_POST_DESCRIPTION',
          title: 'POSTS_REMOVE_POST_TITLE',
          buttons: [
            this.modalAttributesService.createDialogButton('BTN_CLOSE_HINT'),
            this.modalAttributesService.createDialogButton(
              'POSTS_REMOVE_POST_BTN_HINT',
              'btn-primary',
              DialogActionTypes.CONFIRM_ACTION,
            ),
          ],
        },
      },
      () => {
        this.postsService.removePost({ postId: this.post.entity_id });
      },
    );
  }

  public updatePost(event: Event): void {
    this.formStateService.stopAllAction(event);

    if (this.updatePostForm.childForm.isFormValid()) {
      this.formValueService.saveFormValue(this.updatePostForm.childForm.form.value);
      this.updatePostForm.prepareSubmitData();
      this.operationInfoService.setupOperation(
        {
          spinner: 'button',
          button: { id: 'btn-update-post' },
          successfulMessage: 'POST_UPDATED_DESCRIPTION',
        },
        () => {
          this.updatePostService.updatePost(this.formContent.data as UpdatePostRequest, this.post.entity_id);
        },
      );
    }
  }

  private subscribeOnUpdatePostServiceChanges(): void {
    this.subscriptions$.push(
      this.updatePostService.updatePostFormSubject.subscribe((formContent: FormConstructorContent) => {
        this.formContent = formContent;
        setTimeout(() => {
          this.formValueService.saveFormValue(this.updatePostForm.childForm.form.value);
          this.subscribeToControlValueChanges();
        }, 0);
      }),
    );
  }

  private initSocialContex(): void {
    const socialContext: PostSocialContext = {};

    if (this.post.social_context) {
      socialContext[
        [
          SocialContextEntityType.Community,
          SocialContextEntityType.HealthCommunity,
          SocialContextEntityType.CharityCommunity,
        ].includes(this.post.social_context.entity_type)
          ? 'community_id'
          : 'project_id'
      ] = { entity_id: this.post.social_context.entity_id, title: this.post.social_context.entity_title };
    }

    this.updatePostService.fetchAttributeMetadata({ ...this.post, ...socialContext });
  }

  private subscribeToControlValueChanges(): void {
    this.subscriptions$.push(
      combineLatest([this.updatePostForm.childForm.form.valueChanges, this.formValueService.valueChanged]).subscribe(
        ([value, startValue]: [object, object]) => {
          this.isFormChanged = this.formValueService.formChangeDetection(value, startValue);
          safelyDetectChanges(this.cdr);
        },
      ),
    );
  }
}
